import { useCallback, useEffect, useRef, useState } from "react";

const CLAMP_CLASSES = {
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3",
};

const ExpandableText = ({
  text,
  lines = 1,
  className = "",
  label = "full text",
  emptyText = "—",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  const clampClass = CLAMP_CLASSES[lines] || CLAMP_CLASSES[1];

  const measure = useCallback(() => {
    const node = textRef.current;
    // Measuring while expanded always reports "fits", so keep the last result.
    if (!node || expanded) return;

    setIsOverflowing(
      lines === 1
        ? node.scrollWidth > node.clientWidth + 1
        : node.scrollHeight > node.clientHeight + 1
    );
  }, [expanded, lines]);

  useEffect(() => {
    measure();
  }, [measure, text]);

  useEffect(() => {
    const node = textRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  const handleToggle = () => {
    // Don't collapse when the user is selecting the text to copy it.
    const selection = window.getSelection?.();
    if (
      selection &&
      !selection.isCollapsed &&
      textRef.current?.contains(selection.anchorNode)
    ) {
      return;
    }

    setExpanded((previous) => !previous);
  };

  if (!text) {
    return <span className={`block text-gray-400 ${className}`}>{emptyText}</span>;
  }

  const content = (
    <span
      ref={textRef}
      className={`block min-w-0 ${expanded ? "break-words" : clampClass}`}
    >
      {text}
    </span>
  );

  if (!isOverflowing) {
    return <span className={`block min-w-0 ${className}`}>{content}</span>;
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-expanded={expanded}
      aria-label={expanded ? `Collapse ${label}` : `Show ${label}`}
      title={expanded ? undefined : text}
      className={`group flex w-full min-w-0 items-start gap-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${className}`}
    >
      {content}
      <i
        aria-hidden="true"
        className={`fi fi-rr-angle-small-down mt-px shrink-0 text-xs text-gray-400 transition-transform group-hover:text-gray-600 ${
          expanded ? "rotate-180" : ""
        }`}
      />
    </button>
  );
};

export default ExpandableText;
